import React from 'react';
import { EntityTypeEnum, FeaturesEnum } from '@api/ringil-api.v2.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import EllipsisText from '@components/RingilComponents/EllipsisText';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import TransportationTypeTag from '@components/framework/tags/TransportationTypeTag.tsx';
import ExpeditionTypeTag from '@components/framework/tags/ExpeditionTypeTag.tsx';
import ManipulationTag from '@components/framework/tags/ManipulationTag.tsx';
import TruckTag from '@components/framework/tags/TruckTag.tsx';
import CargoTypeTag from '@components/framework/tags/CargoTypeTag.tsx';
import CarrierNoteTag from '@components/framework/tags/CarrierNoteTag.tsx';
import RequirementsTag from '@components/framework/tags/RequirementsTag.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgEdit from '@icons/pencil-inverted.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { findErrorMessage } from '@utils/validationUtils.ts';
import { Form } from '@hooks/useForm/useForm.tsx';
import CapacityTag from '@app/pages/customer/LineEdit/components/CapacityTag.tsx';
import { LineForm } from '@app/pages/customer/LineEdit/types.ts';

interface ComponentProps {
  form: Form<LineForm>;
  companyId: string;
  hasFeature: (features: FeaturesEnum[]) => boolean;
  onEditName: () => void;
}

/**
 * Name, labels and the shipment parameters of a Linka. The expedition header is not reused: half of
 * its tags (state, custom id, incoterms, EKAER/UIT/UTN, Poland SENT, prefill) make no sense on a
 * template, and the Linka adds the template type and the capacity.
 */
const LineHeader: React.FC<ComponentProps> = ({ form, companyId, hasFeature, onEditName }) => {
  const { tr } = useTranslationLgs();
  const errorFor = (...paths: string[]) => paths.map(path => findErrorMessage(form.validationErrors, path, tr)).find(message => message != null);

  return (
    <>
      <div className={'flex min-w-0 flex-row gap-4 items-center'}>
        <COHeadline
          variant={'h2'}
          title={<EllipsisText className="min-w-0">{form.values.name || tr('LineEdit.newLine', 'Nová linka')}</EllipsisText>}
          className="min-w-0 max-w-full"
        />
        <div className={'flex shrink-0 flex-row gap-4 items-center'}>
          <RingilButton mode={'table'} icon={<SvgEdit />} tooltip={tr('LineEdit.editName', 'Upravit název linky')} onClick={onEditName} dataTestId="line-name" />
          <LabelsTag
            objectId={null}
            objectType={EntityTypeEnum.EXPEDITION}
            resolveLabelsForType={EntityTypeEnum.EXPEDITION}
            onChange={labels => form.setFieldValue(form.names.labels, labels)}
            value={form.values.labels}
          />
        </div>
      </div>
      {errorFor('name') && <div className={'text-sm text-complementary-error'}>{errorFor('name')}</div>}

      <div className={'flex flex-col gap-2 my-4'}>
        <Ringil3BadgeRow>
          <ButtonTag label={tr('LineEdit.templateType', 'Typ šablony')} value={tr('LineEdit.templateTypeLine', 'Linka')} readonly />

          <TransportationTypeTag
            transportationType={form.values.transportationType}
            onChange={v =>
              form.useProduce(d => {
                d.transportationType = v;
              })
            }
          />

          <ExpeditionTypeTag
            value={form.values.expeditionType}
            onChange={v =>
              form.useProduce(d => {
                d.expeditionType = v;
              })
            }
          />

          <ManipulationTag
            value={form.values.loadingTypes}
            onChange={v =>
              form.useProduce(d => {
                d.loadingTypes = v;
              })
            }
          />

          <TruckTag
            truckTypeCode={form.values.truckTypeCode}
            onChange={v =>
              form.useProduce(d => {
                d.truckTypeCode = v;
              })
            }
          />

          <CargoTypeTag
            cargoType={form.values.cargoType}
            required
            onChange={v =>
              form.useProduce(d => {
                d.cargoType = v;
              })
            }
            errorMessage={errorFor('cargoType', 'cargoType.cargoTypeCode', 'cargoType.adrWeight')}
          />

          {hasFeature([FeaturesEnum.LADEN_FACTOR]) && (
            <CapacityTag
              value={form.values.capacity}
              onChange={v =>
                form.useProduce(d => {
                  d.capacity = v;
                  if (!v?.ladenFactor) d.allowPartialDelivery = false;
                })
              }
              errorMessage={errorFor('capacity.ladenFactor', 'capacity.cargoTemplateCode')}
            />
          )}

        </Ringil3BadgeRow>
        <Ringil3BadgeRow>
          <RequirementsTag
            valueCodes={form.values.specialRequirements}
            onChange={v =>
              form.useProduce(d => {
                d.specialRequirements = v;
              })
            }
            prefillEnabled={true}
            companyId={companyId}
          />

          <CarrierNoteTag
            value={form.values.carrierNote}
            onChange={v =>
              form.useProduce(d => {
                d.carrierNote = v;
              })
            }
          />
        </Ringil3BadgeRow>
      </div>
    </>
  );
};

export default LineHeader;
